import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, RadioTower } from "lucide-react";
import { useDeviceConfigurationForTenant, useDeviceForTenant, usePatchDeviceConfiguration } from "../../api/devices";
import { useDeviceModels, useDeviceTypes } from "../../api/deviceTypes";
import { deviceModelLabel } from "../../api/deviceModelLabel";
import { extractErrorMessage } from "../../api/errors";
import { getRole, hasMinRole, stopActingAsCompany } from "../../auth/session";
import type { DeviceTypeFieldDefinition, ReportedConfiguration } from "../../api/types";
import { ConfigFieldsForm } from "../../components/config/ConfigFieldsForm";
import { ObjectArrayField } from "../../components/config/ObjectArrayField";
import {
  countObjectArrayValues,
  invalidSecretFields,
  objectArrayValue,
  serializedObjectArrayBytes,
  type ConfigValues,
  type ObjectArrayItemDraft,
} from "../../components/config/configForm";
import { getAdminShadowSettingsRedirectPath } from "./shadowSettingsRouting";
import {
  EYE_ENTRIES_PATH,
  buildGatewayOverrides,
  gatewayFieldVisible,
  describeSource,
  layeredConfigValues,
  layeredEntries,
  limitOverridesToPaths,
  macWarning,
  normaliseEyeEntries,
  omitUnreportedPaths,
  outOfRangeFields,
  pendingPaths,
  readAtPath,
  reportedConfigValues,
  stableStringify,
} from "./gatewayConfig";

// How often the page re-reads the configuration, so the device's side (and the
// pending count) moves on its own while an operator waits for a check-in.
const CONFIG_POLL_MS = 30_000;

function readSafeReturnPath(value: string | null, fallback: string): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return fallback;
  return value;
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function SyncStatus({ reported, pendingCount }: { reported: ReportedConfiguration | null | undefined; pendingCount: number }) {
  if (!reported) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-700 dark:bg-riot-navy-700 dark:text-riot-navy-100">
          Never reported
        </span>
        <span className="text-xs text-gray-500 dark:text-riot-navy-300">
          This gateway has not reported its configuration yet. What you save here is delivered when it next checks in.
        </span>
      </div>
    );
  }
  const { status } = reported;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-riot-navy-200">
        {pendingCount === 0 ? (
          <span className="inline-flex rounded-full bg-green-100 px-2.5 py-0.5 font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300">
            In sync
          </span>
        ) : (
          <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            {pendingCount} pending
          </span>
        )}
        <span>Last reported {formatTime(reported.reportedAt)}</span>
        <span className="text-gray-300 dark:text-riot-navy-600" aria-hidden="true">
          |
        </span>
        <span>
          Last change: {describeSource(status.source)} (rev {status.rev})
        </span>
      </div>
      {status.rejected > 0 && (
        <p className="text-xs font-medium text-red-600 dark:text-red-400">
          The gateway refused {status.rejected} {status.rejected === 1 ? "value" : "values"} in its last update — {status.rejected === 1 ? "it stays" : "they stay"} pending until corrected.
        </p>
      )}
      {pendingCount > 0 && (
        <p className="text-xs text-gray-500 dark:text-riot-navy-400">Pending changes are delivered when the gateway next checks in.</p>
      )}
    </div>
  );
}

export function GatewayShadowSettingsPage() {
  const { imei = "" } = useParams<{ imei: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAdminScope = location.pathname.startsWith("/admin/");
  const scopedTenantId = searchParams.get("tenantId")?.trim() || undefined;
  const returnPath = readSafeReturnPath(searchParams.get("returnTo"), isAdminScope ? "/admin/devices" : "/devices");
  const adminRedirectPath = getAdminShadowSettingsRedirectPath(location.pathname, imei, scopedTenantId, getRole());
  const tenantScopeOptions = { requireTenant: isAdminScope, useActingTenantFallback: !isAdminScope };
  const deviceQuery = useDeviceForTenant(imei, scopedTenantId, tenantScopeOptions);
  const configQuery = useDeviceConfigurationForTenant(imei, scopedTenantId, {
    ...tenantScopeOptions,
    refetchInterval: CONFIG_POLL_MS,
  });
  const patchConfig = usePatchDeviceConfiguration();
  const deviceTypesQuery = useDeviceTypes();
  const deviceModelsQuery = useDeviceModels();

  const [values, setValues] = useState<ConfigValues>({});
  const [entries, setEntries] = useState<ObjectArrayItemDraft[]>([]);
  // Paths the operator edited since the form was seeded. Only these (and the
  // stored override's) are sent while the gateway has never reported.
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  // What the form was seeded from, to notice the configuration moving
  // underneath an edit (a local change adopted, or a save elsewhere).
  const [baseline, setBaseline] = useState<{ rev: number | null; updatedAt: string | undefined }>({
    rev: null,
    updatedAt: undefined,
  });
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const config = configQuery.data?.data;
  const reported = config?.reported ?? null;
  const deviceType = useMemo(
    () => (deviceTypesQuery.data?.data ?? []).find((t) => t.deviceTypeId === config?.deviceTypeId),
    [deviceTypesQuery.data?.data, config?.deviceTypeId],
  );
  // Deprecated fields stay in the schema so stored payloads validate, but are
  // not offered as something to set.
  const formFields = useMemo(() => (deviceType?.fields ?? []).filter((f) => !f.deprecated), [deviceType]);
  const entryField = useMemo(
    () => formFields.find((f) => f.path === EYE_ENTRIES_PATH && f.kind === "object_array"),
    [formFields],
  );
  const flatFields = useMemo(() => formFields.filter((f) => f.kind !== "object_array"), [formFields]);

  useEffect(() => {
    if (!adminRedirectPath) return;
    stopActingAsCompany();
    navigate(adminRedirectPath, { replace: true });
  }, [adminRedirectPath, navigate]);

  // Baseline: what the operator saved, else what the gateway reports it runs,
  // else the contract default - per field, so a partial override still shows
  // the device's real values everywhere else.
  useEffect(() => {
    if (dirty || !config || !deviceType) return;
    const sources = [config.overrides, config.reported?.payload, deviceType.defaultPayload];
    setValues(layeredConfigValues(flatFields, sources));
    setEntries(entryField ? layeredEntries(entryField, sources) : []);
    setTouched(new Set());
    setBaseline({ rev: config.reported?.status.rev ?? null, updatedAt: config.updatedAt });
  }, [config, deviceType, flatFields, entryField, dirty]);

  const movedWhileEditing =
    dirty && ((reported?.status.rev ?? null) !== baseline.rev || config?.updatedAt !== baseline.updatedAt);

  const deviceValues = useMemo(() => reportedConfigValues(flatFields, reported?.payload), [flatFields, reported]);
  const pending = useMemo(
    () => pendingPaths(formFields, config?.overrides, reported?.payload, config?.secrets),
    [formFields, config?.overrides, reported, config?.secrets],
  );
  // Secret values (the web admin password) can be revealed only by a
  // tenant_admin or above, and only when the API sent them.
  const canRevealSecrets = hasMinRole("tenant_admin") && !config?.secretsRedacted;

  // The staged sensor list against the reported one, compared the way the
  // gateway will (normalised, whole list).
  const entriesOnDevice = useMemo(() => {
    if (!entryField || !reported) return null;
    const onDevice = readAtPath(reported.payload, EYE_ENTRIES_PATH);
    if (!onDevice.found) return null;
    const staged = normaliseEyeEntries(objectArrayValue(entryField, entries));
    if (stableStringify(staged) === stableStringify(onDevice.value)) return null;
    const list = Array.isArray(onDevice.value) ? onDevice.value : [];
    const drafts = layeredEntries(entryField, [reported.payload]);
    return { count: list.length, identifiers: countObjectArrayValues(entryField, drafts) };
  }, [entryField, entries, reported]);

  function markDirty(paths: string[]) {
    setDirty(true);
    setSaved(false);
    setTouched((current) => new Set([...current, ...paths]));
  }

  function updateValue(path: string, value: string) {
    markDirty([path]);
    setValues((current) => ({ ...current, [path]: value }));
  }

  function updateEntries(next: ObjectArrayItemDraft[]) {
    markDirty([EYE_ENTRIES_PATH]);
    setEntries(next);
  }

  function discardChanges() {
    setDirty(false);
    setSaved(false);
    setError("");
  }

  // Stages the contract defaults for one section. Nothing reaches the gateway
  // until Save, and the defaults are then sent explicitly: leaving a key out
  // would not reset it, the gateway would keep what it has.
  function resetSection(_section: string, sectionFields: DeviceTypeFieldDefinition[]) {
    markDirty(sectionFields.map((f) => f.path));
    setValues((current) => ({ ...current, ...layeredConfigValues(sectionFields, [deviceType?.defaultPayload]) }));
  }

  function validate(): string {
    if (entryField) {
      const macKeys = (entryField.itemFields ?? []).filter((f) => f.format === "mac").map((f) => f.path);
      if (entries.some((entry) => macKeys.some((key) => macWarning(entry[key] ?? "")))) {
        return "Fix the MAC addresses flagged in the EYE sensor entries.";
      }
      const cap = entryField.maxTotalValues;
      const total = countObjectArrayValues(entryField, entries);
      if (cap && total > cap) {
        return `The gateway accepts at most ${cap} MACs and names across all entries; this list has ${total}.`;
      }
      const byteCap = entryField.maxSerializedBytes;
      const bytes = byteCap ? serializedObjectArrayBytes(entryField, entries) : 0;
      if (byteCap && bytes > byteCap) {
        return `The EYE sensor list is too large for the device shadow (${bytes} bytes, max ${byteCap}) — remove sensors or shorten labels and names.`;
      }
    }
    // Only values that will be sent: before the first report that is what was
    // touched (the untouched defaults, e.g. port 0, are not sent at all); after
    // it, also what the gateway reports (see omitUnreportedPaths).
    const chosen = (f: DeviceTypeFieldDefinition) => touched.has(f.path) || readAtPath(config?.overrides, f.path).found;
    const sent = reported
      ? flatFields.filter((f) => readAtPath(reported.payload, f.path).found || chosen(f))
      : flatFields.filter(chosen);
    // A secret is sent only when typed (see buildGatewayOverrides), so only a
    // typed one is checked - the same rule the backend applies.
    const badSecrets = invalidSecretFields(flatFields, values, touched);
    if (badSecrets.length > 0) {
      return `${badSecrets.map((f) => f.label).join(", ")}: 8–64 characters, letters, digits, punctuation and spaces only, no space at the start or end.`;
    }
    const outOfRange = outOfRangeFields(sent, values, deviceValues);
    if (outOfRange.length > 0) {
      const names = outOfRange.map((f) => `${f.label} (${f.min ?? "…"}–${f.max ?? "…"})`).join(", ");
      return `Outside the range the gateway accepts: ${names}.`;
    }
    return "";
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSaved(false);
    if (isAdminScope && !scopedTenantId) {
      setError("This admin shadow settings page needs a tenant context for the selected device.");
      return;
    }
    if (!deviceType) {
      setError("The gateway's configuration schema could not be loaded, so nothing was saved.");
      return;
    }
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const built = buildGatewayOverrides(formFields, values, entries, touched);
    try {
      await patchConfig.mutateAsync({
        imei,
        tenantId: scopedTenantId,
        // Once the gateway has reported, the whole configuration it reports is
        // sent so desired matches what it runs; a key its firmware does not
        // report yet goes only when chosen. Before that, only what was chosen.
        overrides: reported
          ? omitUnreportedPaths(built, reported.payload, config?.overrides, formFields, touched)
          : limitOverridesToPaths(built, config?.overrides, formFields, touched),
      });
      setDirty(false);
      setSaved(true);
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to save the gateway configuration"));
    }
  }

  const device = deviceQuery.data?.data;
  const isLoading = deviceQuery.isLoading || configQuery.isLoading || deviceTypesQuery.isLoading;

  return (
    <div className="mx-auto max-w-5xl">
      <button
        onClick={() => navigate(returnPath)}
        className="mb-4 flex items-center gap-1 text-xs text-riot-gold-600 hover:underline dark:text-riot-gold-400"
      >
        <ArrowLeft size={14} /> {isAdminScope ? "Back to all devices" : "Back to devices"}
      </button>

      {isAdminScope && !scopedTenantId && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          This admin shadow settings page needs a tenant context for the selected device. Return to All Devices and open Shadow from the device row.
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <header className="border-b border-gray-200 p-5 dark:border-riot-navy-700">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-riot-gold-500">Shadow settings</p>
              <h1 className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">Gateway configuration</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">
                IMEI: <span className="font-mono text-gray-700 dark:text-riot-navy-100">{imei}</span>
                {device?.model ? <span> · {deviceModelLabel(device.model, deviceModelsQuery.data?.data ?? [])}</span> : null}
              </p>
            </div>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-riot-gold-500/15 text-riot-gold-500">
              <RadioTower size={20} />
            </div>
          </div>
          {!isLoading && (
            <div className="mt-4">
              <SyncStatus reported={reported} pendingCount={pending.length} />
            </div>
          )}
        </header>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading gateway configuration...</div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4 p-5">
              <p className="text-xs text-gray-500 dark:text-riot-navy-300">
                {reported
                  ? "This form starts from what the gateway is running — changes made on its own web page are picked up automatically. Saving sends the whole configuration; the gateway applies it at its next check-in."
                  : "Until the gateway reports, the form shows the factory defaults. Saving sends only the settings you change here; everything else stays as the gateway has it, and its first report becomes the baseline."}
              </p>
              {movedWhileEditing && (
                <div
                  role="status"
                  className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300"
                >
                  This gateway&apos;s configuration changed while you were editing
                  {reported ? ` (it now reports rev ${reported.status.rev})` : ""}. Saving sends this form as it is and replaces
                  that change.{" "}
                  <button type="button" onClick={discardChanges} className="font-semibold underline">
                    Discard my edits and reload
                  </button>
                </div>
              )}

              {entryField && (
                <div>
                  <ObjectArrayField
                    field={entryField}
                    items={entries}
                    onChange={updateEntries}
                    idPrefix="gateway-config"
                    itemNoun="sensor entry"
                    headerFieldPath="label"
                    itemWarning={(key, raw) =>
                      entryField.itemFields?.find((f) => f.path === key)?.format === "mac" ? macWarning(raw) : null
                    }
                  />
                  {entriesOnDevice && (
                    <p className="mt-2 text-[11px] font-medium text-amber-700 dark:text-amber-400" data-testid="gateway-entries-on-device">
                      On device: {entriesOnDevice.count} {entriesOnDevice.count === 1 ? "entry" : "entries"},{" "}
                      {entriesOnDevice.identifiers} {entriesOnDevice.identifiers === 1 ? "identifier" : "identifiers"}
                    </p>
                  )}
                </div>
              )}

              {flatFields.length > 0 && (
                <ConfigFieldsForm
                  fields={flatFields}
                  values={values}
                  onChange={updateValue}
                  onResetSection={resetSection}
                  deviceValues={deviceValues}
                  preferNumberInput
                  isFieldVisible={gatewayFieldVisible}
                  compactHints
                  idPrefix="gateway-config"
                  secretStates={config?.secrets}
                  canRevealSecrets={canRevealSecrets}
                />
              )}
            </div>

            {/* Save stays in reach on a long form. */}
            {/* The negative bottom offset covers the scroll area's own bottom
                padding, so content never shows through underneath the bar. */}
            <div className="sticky -bottom-4 z-10 flex flex-wrap items-center gap-2 rounded-b-xl border-t border-gray-200 bg-white/95 px-5 pb-7 pt-3 shadow-[0_-6px_12px_-10px_rgba(0,0,0,0.25)] backdrop-blur dark:border-riot-navy-700 dark:bg-riot-navy-800/95 md:-bottom-5 md:pb-8">
              <button
                type="submit"
                disabled={patchConfig.isPending}
                className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {patchConfig.isPending ? "Saving..." : "Save gateway configuration"}
              </button>
              <button
                type="button"
                onClick={() => navigate(returnPath)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              {dirty && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                  <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
                  Unsaved changes
                </span>
              )}
              {error && <p className="w-full text-sm text-red-600 dark:text-red-400">{error}</p>}
              {saved && (
                <p className="w-full text-sm text-green-700 dark:text-green-300">
                  Gateway configuration saved. It is delivered at the gateway&apos;s next check-in.
                </p>
              )}
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
